import { authService } from '@/services/authService';
import { useAuthStore } from '@/store/authStore';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { zodResolver } from '@hookform/resolvers/zod';
import { FC, useState } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { z } from 'zod';

const registerSchema = z
  .object({
    email: z.string().email('Invalid email address'),
    full_name: z.string().min(2, 'Name must be at least 2 characters'),
    password: z
      .string()
      .min(8, 'Password must be at least 8 characters')
      .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
      .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
      .regex(/[0-9]/, 'Password must contain at least one number'),
    confirmPassword: z.string(),
    tenant_name: z
      .string()
      .min(2, 'Company name must be at least 2 characters'),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
  });

type RegisterFormData = z.infer<typeof registerSchema>;

const RegisterContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(
    135deg,
    var(--primary-600) 0%,
    var(--primary-800) 100%
  );
  padding: 3rem 1.5rem;
`;

const RegisterCard = styled.div`
  max-width: 480px;
  width: 100%;
  background: white;
  border-radius: 1rem;
  padding: 2.5rem;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
`;

const Title = styled.h2`
  margin-top: 0;
  text-align: center;
  font-size: 1.875rem;
  font-weight: 800;
  color: var(--gray-900);
  margin-bottom: 0.5rem;
`;

const Subtitle = styled.p`
  text-align: center;
  font-size: 0.875rem;
  color: var(--gray-600);
  margin-bottom: 2rem;

  a {
    font-weight: 600;
    color: var(--primary-600);
    text-decoration: none;

    &:hover {
      color: var(--primary-700);
    }
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--gray-700);
  margin-bottom: 0.5rem;
`;

const InputWrapper = styled.div`
  position: relative;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 1px solid var(--gray-300);
  border-radius: 0.5rem;
  font-size: 0.875rem;
  color: var(--gray-900);
  transition: all 0.2s;

  &::placeholder {
    color: var(--gray-400);
  }

  &:focus {
    outline: none;
    border-color: var(--primary-600);
    box-shadow: 0 0 0 3px rgba(234, 88, 12, 0.1);
  }

  &:disabled {
    background-color: var(--gray-100);
    cursor: not-allowed;
  }
`;

const PasswordInput = styled(Input)`
  padding-right: 3rem;
`;

const ToggleButton = styled.button`
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  color: var(--gray-400);
  padding: 0.25rem;
  display: flex;
  align-items: center;

  svg {
    width: 1.25rem;
    height: 1.25rem;
  }

  &:hover {
    color: var(--gray-600);
  }
`;

const ErrorText = styled.p`
  margin-top: 0.5rem;
  font-size: 0.875rem;
  color: var(--error-600);
`;

const PasswordStrengthContainer = styled.div`
  margin-top: 0.5rem;
`;

const PasswordStrengthBar = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const StrengthBarTrack = styled.div`
  flex: 1;
  height: 0.5rem;
  background: var(--gray-200);
  border-radius: 9999px;
  overflow: hidden;
`;

const StrengthBarFill = styled.div<{ $strength: number; $color: string }>`
  height: 100%;
  width: ${(props) => (props.$strength / 4) * 100}%;
  background: ${(props) => props.$color};
  transition: all 0.3s;
`;

const StrengthLabel = styled.span`
  font-size: 0.75rem;
  color: var(--gray-600);
  min-width: 3.5rem;
`;

const ErrorAlert = styled.div`
  padding: 1rem;
  background: var(--error-50);
  border-radius: 0.5rem;

  p {
    font-size: 0.875rem;
    color: var(--error-800);
    margin: 0;
  }
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 0.875rem;
  background: var(--primary-600);
  color: white;
  font-weight: 600;
  font-size: 0.875rem;
  border: none;
  border-radius: 0.5rem;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: var(--primary-700);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(234, 88, 12, 0.3);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const Register: FC = () => {
  const navigate = useNavigate();
  const { setAuth, setLoading } = useAuthStore();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError,
    watch,
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
  });

  const password = watch('password');

  const getPasswordStrength = (pwd: string): number => {
    if (!pwd) return 0;
    let strength = 0;
    if (pwd.length >= 8) strength++;
    if (pwd.length >= 12) strength++;
    if (/[A-Z]/.test(pwd)) strength++;
    if (/[a-z]/.test(pwd)) strength++;
    if (/[0-9]/.test(pwd)) strength++;
    if (/[^A-Za-z0-9]/.test(pwd)) strength++;
    return Math.min(strength, 4);
  };

  const passwordStrength = getPasswordStrength(password);
  const strengthLabels = ['', 'Weak', 'Fair', 'Good', 'Strong'];
  const strengthColors = [
    '',
    'var(--error-500)',
    'var(--warning-500)',
    '#3b82f6',
    'var(--success-500)',
  ];

  const onSubmit = async (data: RegisterFormData) => {
    try {
      setLoading(true);
      const { confirmPassword, ...registerData } = data;
      const response = await authService.register(registerData);

      setAuth(response.user, {
        access_token: response.access_token,
        refresh_token: response.refresh_token,
      });

      toast.success('Account created successfully! Welcome aboard!');
      navigate('/dashboard');
    } catch (error: unknown) {
      const err = error as {
        response?: { data?: { error?: string | { message?: string } } };
      };
      let errorMessage = 'Registration failed. Please try again.';

      if (err.response?.data?.error) {
        if (typeof err.response.data.error === 'string') {
          errorMessage = err.response.data.error;
        } else if (
          typeof err.response.data.error === 'object' &&
          err.response.data.error.message
        ) {
          errorMessage = err.response.data.error.message;
        }
      }

      toast.error(errorMessage);
      setError('root', {
        message: errorMessage,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <RegisterContainer>
      <RegisterCard>
        <Title>Create your account</Title>
        <Subtitle>
          Or <Link to="/login">sign in to your existing account</Link>
        </Subtitle>

        <Form onSubmit={handleSubmit(onSubmit)}>
          {/* Full Name */}
          <FormGroup>
            <Label htmlFor="full_name">Full Name</Label>
            <Input
              id="full_name"
              type="text"
              autoComplete="name"
              {...register('full_name')}
              placeholder="John Doe"
            />
            {errors.full_name && (
              <ErrorText>{errors.full_name.message}</ErrorText>
            )}
          </FormGroup>

          {/* Email */}
          <FormGroup>
            <Label htmlFor="email">Email address</Label>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              {...register('email')}
              placeholder="you@example.com"
            />
            {errors.email && <ErrorText>{errors.email.message}</ErrorText>}
          </FormGroup>

          {/* Company Name */}
          <FormGroup>
            <Label htmlFor="tenant_name">Company Name</Label>
            <Input
              id="tenant_name"
              type="text"
              {...register('tenant_name')}
              placeholder="Acme Inc"
            />
            {errors.tenant_name && (
              <ErrorText>{errors.tenant_name.message}</ErrorText>
            )}
          </FormGroup>

          {/* Password */}
          <FormGroup>
            <Label htmlFor="password">Password</Label>
            <InputWrapper>
              <PasswordInput
                id="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                {...register('password')}
                placeholder="••••••••"
              />
              <ToggleButton
                type="button"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeSlashIcon /> : <EyeIcon />}
              </ToggleButton>
            </InputWrapper>
            {errors.password && (
              <ErrorText>{errors.password.message}</ErrorText>
            )}

            {/* Password Strength Indicator */}
            {password && (
              <PasswordStrengthContainer>
                <PasswordStrengthBar>
                  <StrengthBarTrack>
                    <StrengthBarFill
                      $strength={passwordStrength}
                      $color={strengthColors[passwordStrength]}
                    />
                  </StrengthBarTrack>
                  <StrengthLabel>
                    {strengthLabels[passwordStrength]}
                  </StrengthLabel>
                </PasswordStrengthBar>
              </PasswordStrengthContainer>
            )}
          </FormGroup>

          {/* Confirm Password */}
          <FormGroup>
            <Label htmlFor="confirmPassword">Confirm Password</Label>
            <InputWrapper>
              <PasswordInput
                id="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                autoComplete="new-password"
                {...register('confirmPassword')}
                placeholder="••••••••"
              />
              <ToggleButton
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? <EyeSlashIcon /> : <EyeIcon />}
              </ToggleButton>
            </InputWrapper>
            {errors.confirmPassword && (
              <ErrorText>{errors.confirmPassword.message}</ErrorText>
            )}
          </FormGroup>

          {errors.root && (
            <ErrorAlert>
              <p>{errors.root.message}</p>
            </ErrorAlert>
          )}

          <SubmitButton type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Creating account...' : 'Create account'}
          </SubmitButton>
        </Form>
      </RegisterCard>
    </RegisterContainer>
  );
};

export default Register;
